import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  FormControlLabel,
  Switch,
  Alert,
  IconButton,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import TextColorPalette from './shared/TextColorPalette';

interface SliderTextStyle {
  text: string;
  color: string;
  fontSize: string;
  fontFamily: string;
  fontWeight: 'normal' | 'bold';
  fontStyle: 'normal' | 'italic';
}

type SlideButtonAction = 'scroll-to-section' | 'external-url' | 'contact-page' | 'none';

interface SlideButton {
  show?: boolean;
  label?: string;
  action?: SlideButtonAction;
  target?: string;
  style?: 'primary' | 'secondary' | 'outline';
}

interface SlideItem {
  id: string;
  mediaUrl: string;
  mediaType: 'image' | 'video';
  title: string;
  active: boolean;
  mediaPath?: string;
  overrideTitle?: SliderTextStyle;
  overrideDescription?: SliderTextStyle;
  overrideTextPosition?: string;
  overrideTextAnimation?: string;
  buttons?: SlideButton[];
  button?: SlideButton;
}

interface SlideSpecificSettingsModalProps {
  open: boolean;
  onClose: () => void;
  slide: SlideItem | null;
  onSave: (updatedSlide: SlideItem) => void | Promise<void>;
  initialFocus?: 'general' | 'button';
}

const BUTTON_ACTIONS: SlideButtonAction[] = [
  'none',
  'scroll-to-section',
  'external-url',
  'contact-page',
];

const MAX_SLIDE_BUTTONS = 4;

const fontOptions = [
  "Arial, sans-serif",
  "Verdana, sans-serif",
  "Georgia, serif",
  "'Times New Roman', Times, serif",
  "'Courier New', Courier, monospace",
  "Impact, sans-serif",
  "'Comic Sans MS', cursive, sans-serif",
];

const positionOptions = [
  'middle-center',
  'top-center',
  'bottom-center',
  'middle-left',
  'middle-right',
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
];

const animationOptions = [
  'none',
  'fade-in',
  'slide-in-from-bottom',
  'slide-in-from-top',
  'slide-in-from-left',
  'slide-in-from-right',
  'zoom-in'
];

const defaultTextStyle: SliderTextStyle = {
    text: '',
    color: '#FFFFFF',
    fontSize: '48px',
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    fontStyle: 'normal',
};

const emptyButton = (partial?: Partial<SlideButton>): SlideButton => ({
  show: true,
  label: '',
  action: 'external-url',
  target: '',
  style: 'primary',
  ...partial,
});

const normalizeButtons = (slide: SlideItem | null, ensureOne: boolean): SlideButton[] => {
  if (!slide) return ensureOne ? [emptyButton()] : [];
  if (Array.isArray(slide.buttons) && slide.buttons.length > 0) {
    return slide.buttons.map((b) => emptyButton(b));
  }
  if (slide.button) {
    return [emptyButton(slide.button)];
  }
  return ensureOne ? [emptyButton()] : [];
};

const SlideSpecificSettingsModal: React.FC<SlideSpecificSettingsModalProps> = ({
  open,
  onClose,
  slide,
  onSave,
  initialFocus = 'general',
}) => {
  const { t } = useTranslation('salesSystem');
  const [titleSettings, setTitleSettings] = useState<SliderTextStyle>(defaultTextStyle);
  const [descriptionSettings, setDescriptionSettings] = useState<SliderTextStyle>(defaultTextStyle);
  const [textPosition, setTextPosition] = useState('middle-center');
  const [textAnimation, setTextAnimation] = useState('fade-in');
  const [buttons, setButtons] = useState<SlideButton[]>([]);

  useEffect(() => {
    if (slide) {
      setTitleSettings(slide.overrideTitle || { ...defaultTextStyle, text: '' });
      setDescriptionSettings(slide.overrideDescription || { ...defaultTextStyle, fontSize: '18px', fontWeight: 'normal', text: '' });
      setTextPosition(slide.overrideTextPosition || 'middle-center');
      setTextAnimation(slide.overrideTextAnimation || 'fade-in');
      setButtons(normalizeButtons(slide, initialFocus === 'button'));
    } else {
      setTitleSettings(defaultTextStyle);
      setDescriptionSettings({ ...defaultTextStyle, fontSize: '18px', fontWeight: 'normal' });
      setTextPosition('middle-center');
      setTextAnimation('fade-in');
      setButtons([]);
    }
  }, [slide, initialFocus]);

  const patchButtonAt = (index: number, partial: Partial<SlideButton>) => {
    setButtons((prev) => prev.map((b, i) => (i === index ? { ...b, ...partial } : b)));
  };

  const addButton = () => {
    setButtons((prev) => (prev.length >= MAX_SLIDE_BUTTONS ? prev : [...prev, emptyButton()]));
  };

  const removeButton = (index: number) => {
    setButtons((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!slide) return;
    const cleaned = buttons
      .map((b) => ({
        show: Boolean(b.show),
        label: (b.label || '').trim(),
        action: b.action || 'none',
        target: (b.target || '').trim(),
        style: b.style || 'primary',
      }))
      .filter((b) => b.show || b.label);

    const updatedSlide: SlideItem =
      initialFocus === 'button'
        ? {
            ...slide,
            buttons: cleaned,
            button: cleaned[0],
          }
        : {
            ...slide,
            overrideTitle: titleSettings,
            overrideDescription: descriptionSettings,
            overrideTextPosition: textPosition,
            overrideTextAnimation: textAnimation,
            buttons: slide.buttons || (slide.button ? [slide.button] : undefined),
            button: slide.button,
          };
    try {
      await Promise.resolve(onSave(updatedSlide));
    } catch {
      return;
    }
  };

  if (!slide) return null;

  const isButtonOnly = initialFocus === 'button';

  const buttonSection = (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceSlideModal.buttonHint')}
      </Typography>

      {buttons.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('ecommerceSlideModal.buttonEnableHint')}
        </Alert>
      ) : (
        buttons.map((button, index) => {
          const action = button.action || 'none';
          const urlDisabled = action === 'none' || action === 'contact-page';
          return (
            <Box
              key={`slide-btn-${index}`}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2">
                  {t('ecommerceSlideModal.buttonItemTitle', { index: index + 1 })}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(button.show)}
                        onChange={(e) => patchButtonAt(index, { show: e.target.checked })}
                        size="small"
                      />
                    }
                    label={t('ecommerceSlideModal.fields.showButton')}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeButton(index)}
                    aria-label={t('ecommerceSlideModal.actions.removeButton')}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceSlideModal.fields.buttonLabel')}
                    value={button.label || ''}
                    onChange={(e) => patchButtonAt(index, { label: e.target.value })}
                    placeholder={t('ecommerceSlideModal.fields.buttonLabelPlaceholder')}
                    autoFocus={isButtonOnly && index === 0}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('ecommerceSlideModal.fields.buttonAction')}</InputLabel>
                    <Select
                      label={t('ecommerceSlideModal.fields.buttonAction')}
                      value={action}
                      onChange={(e) => patchButtonAt(index, { action: e.target.value as SlideButtonAction })}
                    >
                      {BUTTON_ACTIONS.map((value) => (
                        <MenuItem key={value} value={value}>
                          {t(`ecommerceSlideModal.buttonActions.${value}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('ecommerceSlideModal.fields.buttonStyle')}</InputLabel>
                    <Select
                      label={t('ecommerceSlideModal.fields.buttonStyle')}
                      value={button.style || 'primary'}
                      onChange={(e) =>
                        patchButtonAt(index, { style: e.target.value as 'primary' | 'secondary' | 'outline' })
                      }
                    >
                      <MenuItem value="primary">{t('ecommerceSlideModal.buttonStyles.primary')}</MenuItem>
                      <MenuItem value="secondary">{t('ecommerceSlideModal.buttonStyles.secondary')}</MenuItem>
                      <MenuItem value="outline">{t('ecommerceSlideModal.buttonStyles.outline')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label={
                      action === 'scroll-to-section'
                        ? t('ecommerceSlideModal.fields.buttonSection')
                        : t('ecommerceSlideModal.fields.buttonUrl')
                    }
                    value={button.target || ''}
                    onChange={(e) => patchButtonAt(index, { target: e.target.value })}
                    disabled={urlDisabled}
                    placeholder={
                      action === 'scroll-to-section'
                        ? t('ecommerceSlideModal.fields.buttonSectionPlaceholder')
                        : 'https://... veya /urunler'
                    }
                    helperText={
                      action === 'scroll-to-section'
                        ? t('ecommerceSlideModal.fields.buttonSectionHint')
                        : action === 'contact-page'
                          ? t('ecommerceSlideModal.fields.buttonContactHint')
                          : undefined
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })
      )}

      <Button
        startIcon={<AddIcon />}
        variant="outlined"
        size="small"
        onClick={addButton}
        disabled={buttons.length >= MAX_SLIDE_BUTTONS}
        sx={{ textTransform: 'none' }}
      >
        {t('ecommerceSlideModal.actions.addAnotherButton')}
      </Button>
      {buttons.length >= MAX_SLIDE_BUTTONS && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {t('ecommerceSlideModal.maxButtonsHint', { max: MAX_SLIDE_BUTTONS })}
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth={isButtonOnly ? 'sm' : 'md'} fullWidth>
      <DialogTitle>
        {isButtonOnly
          ? t('ecommerceSlideModal.buttonSettings')
          : t('ecommerceSlideModal.title', { title: slide.title })}
      </DialogTitle>
      <DialogContent>
        {isButtonOnly ? (
          buttonSection
        ) : (
          <>
            <Typography variant="h6" gutterBottom>{t('ecommerceSlideModal.generalSettings')}</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('ecommerceSlideModal.fields.textPosition')}</InputLabel>
                  <Select label={t('ecommerceSlideModal.fields.textPosition')} value={textPosition} onChange={(e) => setTextPosition(e.target.value)}>
                    {positionOptions.map((value) => (
                      <MenuItem key={value} value={value}>
                        {t(`ecommerceSlideModal.positionOptions.${value}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('ecommerceSlideModal.fields.entryAnimation')}</InputLabel>
                  <Select label={t('ecommerceSlideModal.fields.entryAnimation')} value={textAnimation} onChange={(e) => setTextAnimation(e.target.value)}>
                    {animationOptions.map((value) => (
                      <MenuItem key={value} value={value}>
                        {t(`ecommerceSlideModal.animationOptions.${value}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>{t('ecommerceSlideModal.customTitleSettings')}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label={t('ecommerceSlideModal.fields.titleText')} value={titleSettings.text} onChange={(e) => setTitleSettings(s => ({...s, text: e.target.value}))} size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextColorPalette
                  label={t('ecommerceSlideModal.fields.textColor')}
                  value={titleSettings.color}
                  onChange={(color) => setTitleSettings((s) => ({ ...s, color }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label={t('ecommerceSlideModal.fields.fontSize')} type="number" value={parseInt(titleSettings.fontSize) || ''} onChange={(e) => setTitleSettings(s => ({...s, fontSize: `${e.target.value}px`}))} size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small"><InputLabel>{t('ecommerceSlideModal.fields.fontFamily')}</InputLabel><Select label={t('ecommerceSlideModal.fields.fontFamily')} value={titleSettings.fontFamily} onChange={(e) => setTitleSettings(s => ({...s, fontFamily: e.target.value}))}>{fontOptions.map(f => <MenuItem key={f} value={f}>{f.split(',')[0]}</MenuItem>)}</Select></FormControl>
              </Grid>
              <Grid item xs={12}>
                <ToggleButtonGroup size="small" value={[titleSettings.fontWeight, titleSettings.fontStyle]} onChange={(e, newFormats) => setTitleSettings(s => ({...s, fontWeight: newFormats.includes('bold') ? 'bold' : 'normal', fontStyle: newFormats.includes('italic') ? 'italic' : 'normal'}))}>
                  <ToggleButton value="bold"><FormatBoldIcon /></ToggleButton>
                  <ToggleButton value="italic"><FormatItalicIcon /></ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>{t('ecommerceSlideModal.customDescriptionSettings')}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label={t('ecommerceSlideModal.fields.descriptionText')} multiline rows={2} value={descriptionSettings.text} onChange={(e) => setDescriptionSettings(s => ({...s, text: e.target.value}))} size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextColorPalette
                  label={t('ecommerceSlideModal.fields.textColor')}
                  value={descriptionSettings.color}
                  onChange={(color) => setDescriptionSettings((s) => ({ ...s, color }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label={t('ecommerceSlideModal.fields.fontSize')} type="number" value={parseInt(descriptionSettings.fontSize) || ''} onChange={(e) => setDescriptionSettings(s => ({...s, fontSize: `${e.target.value}px`}))} size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small"><InputLabel>{t('ecommerceSlideModal.fields.fontFamily')}</InputLabel><Select label={t('ecommerceSlideModal.fields.fontFamily')} value={descriptionSettings.fontFamily} onChange={(e) => setDescriptionSettings(s => ({...s, fontFamily: e.target.value}))}>{fontOptions.map(f => <MenuItem key={f} value={f}>{f.split(',')[0]}</MenuItem>)}</Select></FormControl>
              </Grid>
              <Grid item xs={12}>
                <ToggleButtonGroup size="small" value={[descriptionSettings.fontWeight, descriptionSettings.fontStyle]} onChange={(e, newFormats) => setDescriptionSettings(s => ({...s, fontWeight: newFormats.includes('bold') ? 'bold' : 'normal', fontStyle: newFormats.includes('italic') ? 'italic' : 'normal'}))}>
                  <ToggleButton value="bold"><FormatBoldIcon /></ToggleButton>
                  <ToggleButton value="italic"><FormatItalicIcon /></ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('ecommerceSlideModal.actions.cancel')}</Button>
        <Button onClick={handleSave} variant="contained">{t('ecommerceSlideModal.actions.save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SlideSpecificSettingsModal;
